import { Add } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Avatar as MuiAvatar,
  Select,
  Option,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Link, Outlet } from "@tanstack/react-router";
import { ApiUserInline, ApiVenueInline, ApiVenueStaffInfo } from "griptonite-api-client";
import { memo, useMemo, useState } from "react";
import { create } from "zustand";
import { combine } from "zustand/middleware";

import Warning from "@/assets/icons/Warning";
import { useMeProfileQuery } from "@/hooks/profile";
import {
  useAllVenueStaffData,
  useAvailableVenuesQuery,
  useUpdateStaffRoleMutation,
} from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import Avatar from "../Avatar";
import { IconButtonLink } from "../Links";

export default function Staff() {
  return <StaffTable />;
}

const VenueMultiSelect = ({
  venues,
  onChange,
}: {
  venues: string[];
  onChange: (venues: string[]) => void;
}) => {
  const { data: availableVenues } = useAvailableVenuesQuery();
  return (
    <Select
      multiple
      value={venues}
      onChange={(_e, value) => onChange(value)}
      placeholder="Select venues..."
      renderValue={(value) => {
        if (!value || value.length === 0) {
          return "Select venues...";
        } else {
          return `${value.length} venues selected`;
        }
      }}
    >
      {availableVenues?.map((venue) => (
        <Option key={venue.uuid} value={venue.uuid}>
          <MuiAvatar
            src={getThumbnailUrl(venue.logo?.url) ?? undefined}
            alt={venue?.name}
            sx={{ width: 24, height: 24, borderRadius: 4 }}
            size="sm"
          />
          <Typography level="body-sm">{venue.name}</Typography>
        </Option>
      ))}
    </Select>
  );
};

const StaffTableRow = memo(
  ({
    staff,
    venues,
  }: {
    staff: {
      user: ApiUserInline;
      hasAnyActiveRole: boolean;
      venueRoles: Map<string, Omit<ApiVenueStaffInfo, "user">>;
    };
    venues?: ApiVenueInline[];
  }) => {
    return (
      <tr key={staff.user.uuid} className={!staff.hasAnyActiveRole ? "deactivated" : ""}>
        <td className="name">
          <Link
            to={`./$userUuid`}
            from="/gym/$venueUuid/staff"
            params={{
              userUuid: staff.user.uuid,
            }}
            style={{
              textDecoration: "none",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              {staff.user.invitePending ? (
                <Tooltip title="Waiting for invite to be accepted" placement={"bottom-end"}>
                  <Badge
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    badgeContent={<Warning sx={{ width: 18, height: 18 }} />}
                    size="sm"
                    variant="plain"
                    color="warning"
                    sx={{
                      "--Badge-paddingX": "0px",
                      ".MuiBadge-badge": {
                        boxShadow: "none",
                        borderRadius: 4,
                        width: 16,
                        backgroundColor: "transparent",
                      },
                    }}
                    badgeInset={"12%"}
                  >
                    <Box sx={{ opacity: 0.5 }}>
                      <Avatar user={staff.user} />
                    </Box>
                  </Badge>
                </Tooltip>
              ) : (
                <Avatar user={staff.user} />
              )}
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography level="title-md">{staff.user.name}</Typography>
                <Typography level="body-xs" color="neutral">
                  {staff.user.email}
                </Typography>
              </Box>
            </Stack>
          </Link>
        </td>
        {venues?.map((venue) => {
          const role = staff.venueRoles.get(venue.uuid);
          return (
            <td key={venue.uuid}>
              <StaffRole role={role} venueUuid={venue.uuid} userUuid={staff.user.uuid} />
            </td>
          );
        })}
      </tr>
    );
  },
);
StaffTableRow.displayName = "StaffTableRow";

const emptyArray = [] as never[];

export const useSelectedVenues = create(
  combine(
    {
      selectedVenues: [] as string[],
    },
    (set) => ({
      setSelectedVenues: (selectedVenues: string[]) => {
        set({ selectedVenues });
      },
    }),
  ),
);

const StaffTable = memo(() => {
  const { selectedVenues, setSelectedVenues } = useSelectedVenues();
  const staffQuery = useAllVenueStaffData(selectedVenues.length ? { venues: selectedVenues } : {});
  const [showDeactivated, setShowDeactivated] = useState(false);

  const venues = staffQuery.data?.venues;
  const { staff, deactivatedCount } = useMemo(() => {
    let res = staffQuery.data?.staff || emptyArray;
    if (!showDeactivated) {
      res = res.filter((s) => s.hasAnyActiveRole);
    }
    return {
      staff: res,
      deactivatedCount: (staffQuery.data?.staff?.length || 0) - res.length,
    };
  }, [staffQuery.data?.staff, showDeactivated]);

  const { data: user } = useMeProfileQuery();

  return (
    <Stack direction="row" sx={{ height: "100dvh" }}>
      <Box
        sx={{
          overflow: "auto",
          flex: 1,
          position: "relative",
        }}
      >
        {user?.isStaff ? (
          <VenueMultiSelect
            venues={selectedVenues}
            onChange={(venues) => setSelectedVenues(venues)}
          />
        ) : null}
        <Table
          hoverRow
          sx={{
            "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
            flex: 1,
            thead: {
              ".line2 th": {
                verticalAlign: "middle",
                height: "auto!important",
              },
            },
            ".name": {
              borderRight: "1px solid",
              borderRightColor: "var(--joy-palette-divider)",
            },
            "th.name": {
              width: 250,
            },
            "td, th": {
              width: 100,
            },
            "tr.deactivated td.name": {
              opacity: 0.5,
            },
            "thead th": {
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
            "thead tr.line2 th": {
              top: 54,
            },
            "thead th.name": {
              left: 0,
              zIndex: 2,
            },
            "tbody tr td.name": {
              position: "sticky",
              left: 0,
              zIndex: 1,
            },
          }}
        >
          <thead>
            <tr className="line1">
              <th className="name"></th>
              {venues?.map((venue) => (
                <th key={venue.uuid}>
                  <MuiAvatar
                    src={getThumbnailUrl(venue.logoUrl) ?? undefined}
                    alt={venue.name}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      borderColor: "neutral.100",
                    }}
                    variant="outlined"
                  />
                </th>
              ))}
            </tr>
            <tr className="line2">
              <th className="name">Name</th>
              {venues?.map((venue) => (
                <th key={venue.uuid}>
                  <Box
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      maxHeight: 34,
                      // width: 100,
                      overflow: "hidden",
                      whiteSpace: "normal",
                      lineHeight: "1.2em",
                    }}
                  >
                    {venue.name}
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff?.map((staff) => (
              <StaffTableRow key={staff.user.uuid} staff={staff} venues={venues} />
            ))}
            {!showDeactivated && deactivatedCount ? (
              <tr>
                <td colSpan={(venues?.length || 0) + 1}>
                  <Button
                    onClick={() => setShowDeactivated(true)}
                    sx={{
                      margin: "0 auto",
                      display: "block",
                    }}
                    variant="outlined"
                    color="neutral"
                  >
                    Show {deactivatedCount} more deactivated
                  </Button>
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
        <IconButtonLink
          from="/gym/$venueUuid/staff"
          to="./add"
          size="lg"
          color="primary"
          variant="solid"
          sx={{
            borderRadius: "50%",
            position: "sticky",
            bottom: 24,
            zIndex: 1,
            left: "100%",
            transform: "translateX(-50%)",
          }}
        >
          <Add />
        </IconButtonLink>
      </Box>
      <Outlet />
    </Stack>
  );
});

StaffTable.displayName = "StaffTable";

export const StaffRoleUi = ({
  role,
  isActive,
  showDeactivate = true,
  onChange,
}: {
  role: "ADMIN" | "STAFF" | "ROUTE_SETTER" | null;
  showDeactivate?: boolean;
  isActive?: boolean;
  onChange: (role: "ADMIN" | "STAFF" | "ROUTE_SETTER" | null, isActive: boolean) => void;
}) => {
  let color = {
    ADMIN: "primary",
    STAFF: "success",
    ROUTE_SETTER: "neutral",
    "": "neutral",
  }[role || ""] as "primary" | "success" | "neutral";
  let label = {
    ADMIN: "Admin",
    STAFF: "Staff",
    ROUTE_SETTER: "Guest Setter",
    "": "–",
  }[role || ""];

  let variant: "solid" | "outlined" = "solid";

  if (isActive === false) {
    label = "Deactivated";
    color = "neutral";
    variant = "outlined";
  }

  const menuItems = [
    {
      label: "Make Admin",
      value: "ADMIN",
    },
    {
      label: "Make Staff",
      value: "STAFF",
    },
    {
      label: "Make Guest Setter",
      value: "ROUTE_SETTER",
    },
    ...(showDeactivate
      ? ([
          {
            label: "Deactivate",
            value: "DEACTIVATE",
          },
        ] as const)
      : ([] as never[])),
  ] as const;

  return (
    <Dropdown>
      <MenuButton slots={{ root: Box }} sx={{ cursor: "pointer" }}>
        {role ? (
          <Chip
            color={color}
            variant={variant}
            sx={
              role === "ADMIN" && isActive
                ? {
                    backgroundColor: "#5F26A7",
                    borderColor: "#5F26A7",
                  }
                : {}
            }
          >
            {label}
          </Chip>
        ) : (
          <Typography textColor="neutral.500" sx={{ minWidth: 32 }}>
            –
          </Typography>
        )}
      </MenuButton>
      <Menu placement="bottom-start">
        {menuItems.map((item) => (
          <MenuItem
            key={item.value}
            color={item.value === "DEACTIVATE" ? "danger" : undefined}
            onClick={() => {
              if (item.value === "DEACTIVATE") {
                onChange(role, false);
              } else {
                onChange(item.value, true);
              }
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
};

export const StaffRole = ({
  role,
  userUuid,
  venueUuid,
}: {
  role: Omit<ApiVenueStaffInfo, "user"> | undefined;
  userUuid: string;
  venueUuid: string;
}) => {
  const mutation = useUpdateStaffRoleMutation();

  return (
    <StaffRoleUi
      role={role?.role ?? null}
      isActive={role?.isActive}
      onChange={(role, isActive) => {
        mutation.mutate(
          {
            venueUuid,
            userUuid,
            role: role ?? undefined,
            isActive,
          },
          {
            onError: () =>
              alert("An unexpected error occurred. Please try again or contact support."),
          },
        );
      }}
      showDeactivate={role?.isActive || false}
    />
  );
};
